/**
 * UTM Tracking Utility
 * Captures UTM parameters from URL and stores them for order attribution
 */

const UTM_STORAGE_KEY = "utm_params";
const UTM_EXPIRY_KEY = "utm_params_expiry";
const UTM_EXPIRY_DAYS = 30; // UTM params expire after 30 days

/**
 * Capture UTM parameters from current URL
 * Stores in sessionStorage and localStorage with expiry
 */
export const captureUTMParams = () => {
  try {
    const params = new URLSearchParams(window.location.search);

    const utm = {
      source: params.get("utm_source"),
      medium: params.get("utm_medium"),
      campaign: params.get("utm_campaign"),
      term: params.get("utm_term"),
      content: params.get("utm_content"),
      timestamp: Date.now(),
      url: window.location.href,
    };

    // Only store if at least one UTM parameter exists
    const hasUTMParams = Object.values(utm).some(
      (value, index) => index < 5 && value !== null,
    );

    if (hasUTMParams) {
      const utmData = JSON.stringify(utm);

      // Store in sessionStorage (survives page refreshes in same session)
      sessionStorage.setItem(UTM_STORAGE_KEY, utmData);

      // Store in localStorage with expiry (survives browser close)
      localStorage.setItem(UTM_STORAGE_KEY, utmData);

      const expiryDate = Date.now() + UTM_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(UTM_EXPIRY_KEY, expiryDate.toString());

      // console.log('[UTM Tracking] Captured UTM parameters:', utm);
      return utm;
    }

    return null;
  } catch (error) {
    console.error("[UTM Tracking] Error capturing UTM parameters:", error);
    return null;
  }
};

/**
 * Get stored UTM parameters
 * Checks sessionStorage first, then localStorage (with expiry check)
 */
export const getUTMParams = () => {
  try {
    // Try sessionStorage first (most recent)
    let utmData = sessionStorage.getItem(UTM_STORAGE_KEY);

    if (utmData) {
      return JSON.parse(utmData);
    }

    // Fall back to localStorage if available and not expired
    utmData = localStorage.getItem(UTM_STORAGE_KEY);
    const expiry = localStorage.getItem(UTM_EXPIRY_KEY);

    if (utmData && expiry) {
      const expiryDate = parseInt(expiry, 10);

      if (Date.now() < expiryDate) {
        // Copy to sessionStorage for faster access
        sessionStorage.setItem(UTM_STORAGE_KEY, utmData);
        return JSON.parse(utmData);
      } else {
        // Expired - clear localStorage
        clearUTMParams();
      }
    }

    return null;
  } catch (error) {
    console.error("[UTM Tracking] Error getting UTM parameters:", error);
    return null;
  }
};

/**
 * Clear stored UTM parameters
 */
export const clearUTMParams = () => {
  try {
    sessionStorage.removeItem(UTM_STORAGE_KEY);
    localStorage.removeItem(UTM_STORAGE_KEY);
    localStorage.removeItem(UTM_EXPIRY_KEY);
    // console.log('[UTM Tracking] Cleared UTM parameters');
  } catch (error) {
    console.error("[UTM Tracking] Error clearing UTM parameters:", error);
  }
};

/**
 * Check if UTM parameters exist
 */
export const hasUTMParams = () => {
  const utm = getUTMParams();
  return utm !== null && (utm.source || utm.medium || utm.campaign);
};

/**
 * Get UTM parameters formatted for order data
 */
export const getUTMParamsForOrder = () => {
  const utm = getUTMParams();

  if (!utm) {
    return null;
  }

  return {
    utmSource: utm.source || null,
    utmMedium: utm.medium || null,
    utmCampaign: utm.campaign || null,
    utmTerm: utm.term || null,
    utmContent: utm.content || null,
    utmTimestamp: utm.timestamp || null,
    utmUrl: utm.url || null,
  };
};

/**
 * Log UTM parameters for debugging
 */
export const logUTMParams = () => {
  const utm = getUTMParams();

  if (utm) {
    // console.log('[UTM Tracking] Current UTM parameters:', utm);

    const expiry = localStorage.getItem(UTM_EXPIRY_KEY);
    if (expiry) {
      const expiryDate = new Date(parseInt(expiry, 10));
      // console.log('[UTM Tracking] Expires:', expiryDate.toLocaleString());
    }
  } else {
    console.log("[UTM Tracking] No UTM parameters stored");
  }
};
